import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { canFinance } from '../lib/roles.ts';
import { Icon } from './Icon.tsx';
import { t } from '@/lib/strings';

type NavItem =
  | { kind: 'link'; to: string; label: string; icon: string; matchPrefix?: string }
  | { kind: 'action'; action: 'logout'; label: string; icon: string };

export function BottomNav(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!user) return null;

  const canManageOrg = user.isOrgAdmin || user.role === 'SYS_ADMIN' || user.role === 'ADMIN';
  const showFinance = canFinance(user.role);
  const items: NavItem[] = [
    { kind: 'link', to: '/', label: t.nav.dashboard, icon: 'dashboard' },
    { kind: 'link', to: '/gsr', label: t.nav.gsrs, icon: 'description', matchPrefix: '/gsr' },
    { kind: 'link', to: '/vendors', label: t.nav.vendors, icon: 'apartment', matchPrefix: '/vendors' },
    ...(showFinance ? [{ kind: 'link' as const, to: '/payments', label: t.nav.finance, icon: 'wallet', matchPrefix: '/payments' }] : []),
    ...(canManageOrg ? [{ kind: 'link' as const, to: '/org-settings', label: t.orgSettings.navLabel, icon: 'settings', matchPrefix: '/org-settings' }] : []),
    { kind: 'action', action: 'logout', label: t.nav.signOut, icon: 'logout' },
  ];

  const itemClasses = 'flex h-14 w-full flex-col items-center justify-center gap-1 active:scale-95 transition-transform';

  return (
    <nav
      aria-label={t.nav.mainNav}
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[hsl(var(--chrome))]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex items-stretch">
        {items.map((item) => {
          const active =
            item.kind === 'link'
              ? item.matchPrefix
                ? pathname.startsWith(item.matchPrefix)
                : pathname === item.to
              : false;

          const inner = (
            <>
              <span className="relative inline-flex items-center justify-center">
                <span
                  className={[
                    'inline-flex items-center justify-center rounded-full px-4 py-0.5 transition-colors',
                    active ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]' : 'text-muted-foreground',
                  ].join(' ')}
                >
                  <Icon name={item.icon} size={22} fill={active} />
                </span>
              </span>
              <span className={active ? 'text-[11px] leading-none text-ink font-medium' : 'text-[10.5px] leading-none text-muted-foreground'}>
                {item.label}
              </span>
            </>
          );

          if (item.kind === 'action') {
            return (
              <li key="logout" className="flex-1">
                <button
                  type="button"
                  onClick={logout}
                  aria-label={item.label}
                  className={itemClasses}
                >
                  {inner}
                </button>
              </li>
            );
          }

          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={itemClasses}
              >
                {inner}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
